import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const NotFoundPage = () => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(10);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const particles = Array(30).fill().map((_, i) => ({
    id: i,
    size: Math.random() * 8 + 2,
    x: Math.random() * 100,
    y: Math.random() * 100,
    speed: Math.random() * 1.5 + 0.5,
    opacity: Math.random() * 0.8 + 0.2,
  }));

  // Redirect countdown
  useEffect(() => {
    const redirectTimer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(redirectTimer);
          navigate('/login');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(redirectTimer);
  }, [navigate]);

  // Mouse parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (containerRef.current) {
        const { clientX, clientY } = e;
        const { width, height } = containerRef.current.getBoundingClientRect();
        const x = (clientX / width) - 0.5;
        const y = (clientY / height) - 0.5;
        setMousePosition({ x, y });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Typing effect for the message
  const [displayedText, setDisplayedText] = useState('');
  const fullText = "The page you're looking for doesn't exist or has been moved.";

  useEffect(() => {
    if (displayedText.length < fullText.length) {
      const typingTimer = setTimeout(() => {
        setDisplayedText(fullText.slice(0, displayedText.length + 1));
      }, 50);
      return () => clearTimeout(typingTimer);
    }
  }, [displayedText, fullText]);

  return (
    <div className="bg-gray-900 h-screen w-full flex items-center justify-center overflow-hidden relative">
      {/* Animated particles background */}
      <div className="absolute inset-0 z-0">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute rounded-full bg-cyan-500 animate-float"
            style={{
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              opacity: particle.opacity,
              animationDuration: `${10 + particle.speed * 5}s`,
              animationDelay: `${particle.id * 0.1}s`,
            }}
          />
        ))}
      </div>
      
      {/* Grid overlay */}
      <div className="absolute inset-0 z-0 bg-grid-pattern opacity-10"></div>
      
      {/* Main content */}
      <div 
        ref={containerRef}
        className="relative z-10 max-w-md w-full px-6 py-12 text-center"
        style={{
          transform: `translateX(${mousePosition.x * 20}px) translateY(${mousePosition.y * 20}px)`,
          transition: 'transform 0.1s ease-out',
        }}
      >
        {/* Glitch effect 404 */}
        <div className="relative mb-6 inline-block">
          <h1 className="text-9xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600 animate-pulse">
            404
          </h1>
          <h1 className="absolute top-0 left-0 text-9xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600 opacity-70 animate-glitch-1">
            404
          </h1>
          <h1 className="absolute top-0 left-0 text-9xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-cyan-400 opacity-70 animate-glitch-2">
            404
          </h1>
        </div>
        
        <h2 className="text-2xl font-medium text-white mb-4 animate-fade-in-up">Page Not Found</h2>
        
        {/* Typing effect */}
        <p className="text-gray-300 mb-8 h-12">
          {displayedText}
          <span className="inline-block w-1 h-4 bg-cyan-500 ml-1 animate-blink"></span>
        </p>
        
        {/* Button with hover effect */}
        <button 
          onClick={() => navigate('/login')}
          className="relative px-6 py-3 text-white font-medium overflow-hidden group bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg shadow-lg transition-all duration-300 ease-out hover:shadow-cyan-500/50"
        >
          <span className="absolute right-0 w-8 h-32 -mt-12 transition-all duration-1000 transform translate-x-12 bg-white opacity-10 rotate-12 group-hover:-translate-x-40 ease"></span>
          <span className="relative">Return to Login</span>
        </button>
        
        {/* Countdown timer */}
        <div className="mt-8 text-gray-400 animate-fade-in">
          Redirecting to login in <span className="text-cyan-400 font-medium">{countdown}</span> seconds
        </div>
        
        {/* Circular progress */}
        <div className="mt-4 w-12 h-12 relative mx-auto">
          <svg className="w-full h-full" viewBox="0 0 100 100">
            <circle 
              className="text-gray-700 stroke-current" 
              strokeWidth="8" 
              cx="50" 
              cy="50" 
              r="40" 
              fill="transparent"
            />
            <circle 
              className="text-cyan-500 stroke-current animate-pulse" 
              strokeWidth="8" 
              strokeLinecap="round" 
              cx="50" 
              cy="50" 
              r="40" 
              fill="transparent" 
              strokeDasharray="251.2" 
              strokeDashoffset={251.2 * (1 - countdown / 10)}
              style={{
                transition: "stroke-dashoffset 1s linear"
              }}
            />
          </svg>
          <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center text-cyan-500 font-medium">
            {countdown}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFoundPage;